import { createSelector } from 'reselect';
import {
  AirSegmentDetails,
  CarSegmentDetails,
  HotelSegmentDetails,
  OptionDetailsTypes,
  OptionId,
} from '../../../models/itinerary';
import { pluck, unique } from '../../../utils/helpers';
import { AppState } from '../../model';
import { getSegmentType } from '../itinerary/selectors';
import {
  AirFareOption,
  CarOption,
  CarType,
  FlightOption,
  HotelOption,
  HotelRateOption,
  OptionTypes,
  SegmentTypes,
} from './../../../models/itinerary';
import { airRates, carRates, hotelRates } from './../selected-rate/selectors';

/* -------------------------------------------------------------------------- */
/*                                  Segments                                  */
/* -------------------------------------------------------------------------- */

/**
 * Get all segment options
 */
const getSegments = (state: AppState) => state.segmentOptions;

/**
 * Find segment by segment id
 */
export const findSegment = (segmentId: number) =>
  createSelector(
    getSegments,
    segments => segments[segmentId],
  );

/* -------------------------------------------------------------------------- */
/*                               Segment options                              */
/* -------------------------------------------------------------------------- */

/**
 * Get options for a given segment
 */
const getSegmentOptions = (segmentId: number) =>
  createSelector(
    findSegment(segmentId),
    segment => segment.options,
  );

/**
 * Get segment option
 */
export const getSegmentOption = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOptions(segmentId),
    (options): OptionDetailsTypes => options[optionId],
  );

/* -------------------------------------------------------------------------- */
/*                                  Providers                                 */
/* -------------------------------------------------------------------------- */

type GetAirProviders = (segment: AirSegmentDetails) => Provider[];
type GetHotelProviders = (segment: HotelSegmentDetails) => Provider[];
type GetCarProviders = (segment: CarSegmentDetails) => Provider[];

interface Provider {
  name: string;
  code: string;
}

type GetProviders = GetAirProviders | GetHotelProviders | GetCarProviders;
type GetOptionFromProviderSelector<
  Selector extends GetProviders
> = ArgumentType<Selector, 0>;
// type GetObjFun<O extends Obj> = Filter<Functions, (obj: Obj) => any>;

type GetProvidersSelector<Obj> = Filter<(obj: Obj) => any, GetProviders>;

type GetProvidersByType<T extends SegmentTypes> = T extends 'Car'
  ? GetCarProviders
  : T extends 'Hotel'
  ? GetHotelProviders
  : T extends 'Air'
  ? GetAirProviders
  : never;

const getGetProvidersSelector = <
  SegmentType extends SegmentTypes,
  Selector extends GetProvidersByType<SegmentType>
>(
  type: SegmentTypes,
): Selector => {
  switch (type) {
    case 'Car':
      return getCarProviders as Selector;
    case 'Hotel':
      return getHotelProviders as Selector;
    case 'Air':
    default:
      return getAirProviders as Selector;
  }
};

/**
 * Typescripts interpretation of arguments (currently correct):
 *
 * - `type func: FuncBlue | FuncRed`
 * - `type Obj: ObjBlue | ObjRed`
 */
const getProviders = <
  F extends GetProviders,
  Selector extends GetProvidersSelector<Option>,
  Option extends GetOptionFromProviderSelector<F>
>(
  selector: Selector,
  option: Option,
): ReturnType<F> => {
  return selector(option);
};

export const createGetProviders = (segmentId: number, optionId: string) =>
  createSelector(
    createSelector(
      getSegmentType(segmentId),
      getGetProvidersSelector,
    ),
    getSegmentOption(segmentId, optionId),
    (selector, option) => getProviders(selector as any, option),
  );

type Filter<T, U> = T extends U ? T : never;
type ArgumentType<T, N extends number> = T extends (...args: infer U) => any
  ? U[N]
  : never;

/**
 * Get list of providers for an Air segment option
 */
const getAirProviders: GetAirProviders = ({
  legs,
}: AirSegmentDetails): Provider[] => {
  const providers = legs.map(({ airlineCode, airlineName }) => ({
    code: airlineCode,
    name: airlineName,
  }));

  return unique(providers, (array, item) => {
    // remove duplicates
    return array.findIndex(
      ({ code, name }) => code === item.code && name === item.name,
    );
  });
};

/**
 * Get list of provider codes for a Hotel segment option
 */
const getHotelProviders: GetHotelProviders = ({
  chainCode,
  chainName,
}: HotelSegmentDetails) => {
  return [{ code: chainCode, name: chainName }];
};

/**
 * Get list of provider codes for a Car segment option
 */
const getCarProviders: GetCarProviders = ({
  supplierCode,
  supplierName,
}: CarSegmentDetails) => {
  return [{ code: supplierCode, name: supplierName }];
};

/* -------------------------------------------------------------------------- */
/*                               Provider names                               */
/* -------------------------------------------------------------------------- */

/**
 * Get list of provider codes for an Air segment option
 */
const getAirProviderNames = ({ legs }: AirSegmentDetails) => {
  return unique(pluck('airlineName', legs));
};

/**
 * Get list of provider codes for a Hotel segment option
 */
const getHotelProviderNames = ({ name }: HotelSegmentDetails) => {
  return [name];
};

/**
 * Get list of provider codes for a Car segment option
 */
const getCarProviderNames = ({ supplierName }: CarSegmentDetails) => {
  return [supplierName];
};

/**
 * Get list of provider codes for a given segment option
 */
export const getProviderNames = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOption(segmentId, optionId),
    getSegmentType(segmentId),
    (option: OptionDetailsTypes, type: SegmentTypes | undefined) => {
      switch (type) {
        case 'Car':
          return getCarProviderNames(option as CarSegmentDetails);
        case 'Hotel':
          return getHotelProviderNames(option as HotelSegmentDetails);
        case 'Air':
          return getAirProviderNames(option as AirSegmentDetails);
        default:
          return [];
      }
    },
  );

/* -------------------------------------------------------------------------- */
/*                                Flight numbers                              */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                                    Rates                                   */
/* -------------------------------------------------------------------------- */

export const getRates = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOption(segmentId, optionId),
    getSegmentType(segmentId),
    (segmentOption, type) => {
      switch (type) {
        case 'Air':
          return getAirRates(segmentOption as AirSegmentDetails);
        case 'Car':
          return getCarRates(segmentOption as CarSegmentDetails);
        case 'Hotel':
          return getHotelRates(segmentOption as HotelSegmentDetails);
      }
    },
  );

export const getSelectedRate = (segmentId: number, option: OptionTypes) =>
  createSelector(
    findSegment(segmentId),
    ({ options, type }) => {
      switch (type) {
        case 'Air':
          const { fareId, flightId } = option as FlightOption;
          const airSegment = options[flightId] as AirSegmentDetails;
          return findAirRate(airRates(airSegment), fareId);
        case 'Hotel':
          const { hotelId, rateId } = option as HotelOption;
          const hotelSegment = options[hotelId] as HotelSegmentDetails;
          return findHotelRate(hotelRates(hotelSegment), rateId);
        case 'Car':
          const { carId, supplierCode } = option as CarOption;
          const carSegment = options[supplierCode] as CarSegmentDetails;
          return findCarRate(carRates(carSegment), carId);
      }
    },
  );

/**
 * Get Air rates from option
 */
const getAirRates = ({ fareOptions }: AirSegmentDetails) => {
  return fareOptions;
};

/**
 * Get Car rates from option
 */
const getCarRates = ({ carTypes }: CarSegmentDetails) => {
  return carTypes;
};

/**
 * Get hotel rates from option
 */
const getHotelRates = ({ rates }: HotelSegmentDetails) => {
  return rates;
};

/**
 * Find Air rate by fareId
 */
const findAirRate = (rates: AirFareOption[], rateId: number) => {
  return rates.find(({ fareId: id }) => id === rateId);
};

/**
 * Find Car rate by carId
 */
const findCarRate = (rates: CarType[], rateId: number) => {
  return rates.find(({ carId: id }) => id === rateId);
};

/**
 * Find Hotel rate by rateId
 */
const findHotelRate = (rates: HotelRateOption[], rateId: number) => {
  return rates.find(({ rateId: id }) => id === rateId);
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

export const getOptionType = (option: OptionTypes) => {
  if ((option as FlightOption).flightId) return 'Air';
  if ((option as HotelOption).hotelId) return 'Hotel';
  if ((option as CarOption).carId) return 'Car';
};

// type ObjWithA = { a: string };
// type ObjWithB = { b: string };

// type FunctionA = (obj: ObjWithA) => void;
// type FunctionB = (obj: ObjWithB) => void;

// type Functions = FunctionA | FunctionB;

// type Obj = ObjWithA | ObjWithB;

// /** At this point Typescript treats:
//  * - `Functions` as `FunctionA | FunctionB`
//  * - `Obj` as `ObjA | ObjB`
//  **/
// const callbacks = (func: Functions, obj: Obj) => {
//   /**
//    * What func should be at this point:
//    * `(obj: ObjWithA) => void | (obj: ObjWithB) => void;`
//    *
//    * What Typescript thinks the type for func is:
//    * `(obj: ObjWithA & ObjWithB ) => void;`
//    */
//   func(obj);
// };

// const objWithA: ObjWithA = { a: 'a' };
// const consoleObj = (obj: Obj) => {
//   console.log(obj);
// };

// callbacks(consoleObj, objWithA);
